import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { RBACProvider } from "./contexts/RBACContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MeetingDetailPage from "./pages/MeetingDetailPage";
import MinutesEditorPage from "./pages/MinutesEditorPage";
import SettingsPage from "./pages/SettingsPage";
import PricingPage from "./pages/PricingPage";
import BillingPage from "./pages/BillingPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import BrandingPage from "./pages/BrandingPage";
import ReferralsPage from "./pages/ReferralsPage";
import CompliancePage from "./pages/CompliancePage";
import CompanyCompliancePage from "./pages/CompanyCompliancePage";
import AuditLogPage from "./pages/AuditLogPage";
import AuditCenterPage from "./pages/AuditCenterPage";
import LegalHoldsPage from "./pages/LegalHoldsPage";
import CalendarPage from "./pages/CalendarPage";
import ActionRegisterPage from "./pages/ActionRegisterPage";
import RoleManagementPage from "./pages/RoleManagementPage";
// Documentation Pages
import DocsPage from "./pages/DocsPage";
import UserGuidePage from "./pages/UserGuidePage";
import InvestorPage from "./pages/InvestorPage";
import AboutPage from "./pages/AboutPage";
import "./App.css";

// Component to apply branding dynamically
const BrandingEffect = () => {
  const { companyBranding } = useAuth();
  
  useEffect(() => {
    if (companyBranding && companyBranding.colors) {
      const root = document.documentElement;
      const colors = companyBranding.colors;
      
      // Helper to convert hex to HSL
      const hexToHSL = (hex) => {
        if (!hex) return null;
        hex = hex.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16) / 255;
        const g = parseInt(hex.substring(2, 4), 16) / 255;
        const b = parseInt(hex.substring(4, 6), 16) / 255;
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
          h = s = 0;
        } else {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
            default: h = 0;
          }
        }
        return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
      };
      
      // Check if background is dark
      const isColorDark = (hex) => {
        if (!hex) return false;
        hex = hex.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance < 0.5;
      };
      
      // Apply primary color
      if (colors.primary) {
        const hsl = hexToHSL(colors.primary);
        if (hsl) root.style.setProperty('--primary', hsl);
        root.style.setProperty('--primary-700', colors.primary);
      }
      
      // Apply secondary color
      if (colors.secondary) {
        const hsl = hexToHSL(colors.secondary);
        if (hsl) root.style.setProperty('--secondary', hsl);
        root.style.setProperty('--secondary-600', colors.secondary);
      }
      
      // Apply accent
      if (colors.accent) {
        root.style.setProperty('--accent-color', colors.accent);
      }
      
      // Apply background
      if (colors.background) {
        root.style.setProperty('--background-color', colors.background);
        document.body.style.backgroundColor = colors.background;
        document.body.style.setProperty('background-color', colors.background, 'important');
        
        // Also update the main container
        const mainContent = document.querySelector('main');
        if (mainContent) {
          mainContent.style.backgroundColor = colors.background;
        }
        
        // Toggle dark mode based on background
        if (isColorDark(colors.background)) {
          root.classList.add('dark');
          document.body.classList.add('dark');
        } else {
          root.classList.remove('dark');
          document.body.classList.remove('dark');
        }
      }
      
      // Apply text color
      if (colors.text) {
        root.style.setProperty('--text-color', colors.text);
        document.body.style.color = colors.text;
        document.body.style.setProperty('color', colors.text, 'important');
      }
      
      // Apply a global style override for primary colors
      let brandStyle = document.getElementById('branding-override-style');
      if (!brandStyle) {
        brandStyle = document.createElement('style');
        brandStyle.id = 'branding-override-style';
        document.head.appendChild(brandStyle);
      }
      brandStyle.textContent = `
        :root {
          --primary-700: ${colors.primary || '#0e4f55'} !important;
          --secondary-600: ${colors.secondary || '#64748b'} !important;
        }
        .bg-primary-700, [class*="bg-primary"] {
          background-color: ${colors.primary || '#0e4f55'} !important;
        }
        .text-primary-700, [class*="text-primary"] {
          color: ${colors.primary || '#0e4f55'} !important;
        }
        .border-primary-700 {
          border-color: ${colors.primary || '#0e4f55'} !important;
        }
        .bg-teal-600, .bg-teal-700, .hover\\:bg-teal-700:hover {
          background-color: ${colors.primary || '#0e4f55'} !important;
        }
        .text-teal-600, .text-teal-700 {
          color: ${colors.primary || '#0e4f55'} !important;
        }
        ${isColorDark(colors.background) ? `
        /* Dark mode overrides */
        body, .min-h-screen {
          background-color: ${colors.background} !important;
          color: ${colors.text} !important;
        }
        .bg-stone-50, .bg-slate-50, .bg-gray-50, .bg-white {
          background-color: ${colors.background} !important;
        }
        .bg-stone-100, .bg-slate-100, .bg-gray-100 {
          background-color: #374151 !important;
        }
        .text-stone-900, .text-slate-900, .text-gray-900,
        .text-stone-800, .text-slate-800, .text-gray-800,
        .text-stone-700, .text-slate-700 {
          color: ${colors.text} !important;
        }
        .text-stone-600, .text-slate-600, .text-gray-600,
        .text-stone-500, .text-slate-500 {
          color: #d1d5db !important;
        }
        .border-stone-200, .border-slate-200, .border-gray-200 {
          border-color: #4b5563 !important;
        }
        header, .border-b {
          border-color: #4b5563 !important;
        }
        .shadow, .shadow-sm, .shadow-lg {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3) !important;
        }
        ` : ''}
      `;
      
      // Apply favicon
      if (companyBranding.favicon_url) {
        const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = companyBranding.favicon_url;
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      
      // Apply custom CSS
      if (companyBranding.custom_css) {
        let styleEl = document.getElementById('custom-branding-css');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'custom-branding-css';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = companyBranding.custom_css;
      }
      
      console.log('Branding applied:', { colors, company: companyBranding.company_name });
    }
  }, [companyBranding]);
  
  return null;
};

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Role-based access control
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === "platform_admin") {
      return <Navigate to="/platform" replace />;
    } else if (user.role === "sales_director") {
      return <Navigate to="/referrals" replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Redirect authenticated users to their appropriate dashboard
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect based on role
  if (user.role === "platform_admin") {
    return <Navigate to="/platform" replace />;
  } else if (user.role === "sales_director") {
    return <Navigate to="/referrals" replace />;
  }
  
  // Default: company_admin or company_user goes to dashboard
  return <DashboardPage />;
};

function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <BrandingEffect />
        <RBACProvider>
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<LoginPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              
              {/* Documentation routes (public) */}
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/docs/user-guide" element={<UserGuidePage />} />
              <Route path="/docs/investors" element={<InvestorPage />} />
              <Route path="/docs/about" element={<AboutPage />} />
              
              {/* Role-based dashboard redirect */}
              <Route path="/" element={<RoleBasedRedirect />} />
            
            {/* Platform Admin routes */}
            <Route
              path="/platform"
              element={
                <ProtectedRoute allowedRoles={["platform_admin"]}>
                  <PlatformAdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform/compliance"
              element={
                <ProtectedRoute allowedRoles={["platform_admin"]}>
                  <CompliancePage />
                </ProtectedRoute>
            }
          />
          
          {/* Sales Director routes */}
          <Route
            path="/referrals"
            element={
              <ProtectedRoute allowedRoles={["platform_admin", "sales_director"]}>
                <ReferralsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Calendar */}
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          
          {/* Company Admin routes */}
          <Route
            path="/branding"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <BrandingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compliance"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <CompanyCompliancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <AuditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/legal-holds"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <LegalHoldsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Company user routes */}
          <Route
            path="/meetings/:meetingId"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "company_user", "admin"]}>
                <MeetingDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings/:meetingId/minutes"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "company_user", "admin"]}>
                <MinutesEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "company_user", "admin"]}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          
          {/* Action Register - Premium Feature */}
          <Route
            path="/action-register"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "company_user", "admin"]}>
                <ActionRegisterPage />
              </ProtectedRoute>
            }
          />
          
          {/* Role Management - Admin Only */}
          <Route
            path="/roles"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <RoleManagementPage />
              </ProtectedRoute>
            }
          />
          
          {/* Audit Center - Admin Only */}
          <Route
            path="/audit-center"
            element={
              <ProtectedRoute allowedRoles={["company_admin", "admin"]}>
                <AuditCenterPage />
              </ProtectedRoute>
            }
          />
          
          {/* Catch all - redirect to role-based home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </RBACProvider>
    </AuthProvider>
  </TenantProvider>
  );
}

export default App;
